import React from 'react';
import gql from 'graphql-tag';
import { format } from 'date-fns';
import { useQuery } from '@apollo/react-hooks';
import client from '../../../utils/apolloClient';

const formatDateFilter = date => format(new Date(date), 'dd-MM-yyyy');

const searchTableBookingDashboardQuery = gql`
  query searchTableBookingDashboardQuery($input: SearchInput) {
    search_table_booking_dashboard(input: $input) {
      total_pages
      total_size
      table_booking_dashboard {
        place_name
        place_status
        payment_methods
        total_bookings
        confirmed_count
        cancelled_count
        total_guests
        payment
        payout
        c_fees
        refund_amount
        place_id
        city
      }
    }
  }
`;

const transformBookingDashboardData = bookingSummary => ({
  Name: bookingSummary.place_name,
  City: bookingSummary?.city,
  Bookings: bookingSummary.total_bookings,
  'Confirmed Bookings': bookingSummary.confirmed_count,
  'Cancelled Bookings': bookingSummary.cancelled_count,
  Guests: bookingSummary.total_guests,
  Payment: !bookingSummary.payment
    ? '-'
    : `$${parseFloat(bookingSummary.payment).toFixed(2)}`,
  Payout: !bookingSummary.payout
    ? '-'
    : `$${parseFloat(bookingSummary.payout).toFixed(2)}`,
  'C Fees': !bookingSummary.c_fees
    ? '-'
    : `$${parseFloat(bookingSummary.c_fees).toFixed(2)}`,
  Refund: !bookingSummary.refund_amount
    ? '-'
    : `$${Math.abs(parseFloat(bookingSummary.refund_amount)).toFixed(2)}`,
});

export const fetchTableBookingDashboardData = async ({
  // adminUserId,
  from,
  size = 50,
  sort = null,
  city = null,
  placeId = null,
  bookingDate = null,
  startDate = null,
  endDate = null,
  dateFilterDisabled = true,
}) => {
  const input = {
    // user_id: adminUserId,
    from,
    size,
    sort: sort ?? 'PLACE_NAME_ASC',
    filter: {
      table_booking_filter: {},
      place_filter: {},
    },
  };

  if (city) {
    input.filter.table_booking_filter.city = city;
  }

  if (placeId) {
    input.filter.table_booking_filter.place_id = placeId;
  }

  if (bookingDate) {
    input.filter.table_booking_filter.booking_date = new Date(bookingDate)
      .toLocaleDateString('en-GB')
      .split('/')
      .join('-');
  } else if (startDate && endDate) {
    input.filter.table_booking_filter.date_range = {
      start_date: formatDateFilter(startDate),
      end_date: formatDateFilter(endDate),
    };
  }

  if (dateFilterDisabled) {
    delete input?.filter?.table_booking_filter?.booking_date;
    delete input?.filter?.table_booking_filter?.date_range;
  }

  try {
    const data = await client.clientPublic.query({
      client: client.clientPublic,
      query: searchTableBookingDashboardQuery,
      variables: {
        input,
      },
      fetchPolicy: 'network-only',
    });

    if (
      data &&
      data?.data?.search_table_booking_dashboard &&
      Array.isArray(
        data.data.search_table_booking_dashboard?.table_booking_dashboard,
      ) &&
      data.data.search_table_booking_dashboard?.table_booking_dashboard
        .length !== 0
    ) {
      return data.data.search_table_booking_dashboard;
    }
    return null;
  } catch (error) {
    console.log(error);
    return null;
  }
};

export const useTableBookingDashboardData = ({
  adminUserId,
  from,
  size = 50,
  sort = null,
  city = null,
  placeId = null,
  bookingDate = null,
  startDate = null,
  endDate = null,
  dateFilterDisabled = true,
}) => {
  const [
    loadingTableBookingDashboardData,
    setLoadingTableBookingDashboardData,
  ] = React.useState(true);
  const [
    tableBookingDashboardData,
    settableBookingDashboardData,
  ] = React.useState([]);
  const [totalSize, setTotalSize] = React.useState(0);
  const [totalPages, setTotalPages] = React.useState(0);
  const loadDataRef = React.useRef(true);

  React.useEffect(() => {
    loadDataRef.current = true;
    const fetchFunc = async () => {
      try {
        setLoadingTableBookingDashboardData(true);
        settableBookingDashboardData([]);
        setTotalSize(0);
        setTotalPages(0);

        const fetchTableBookingDashboardDataResponse = await fetchTableBookingDashboardData(
          {
            adminUserId,
            from,
            size,
            sort,
            city,
            placeId,
            bookingDate,
            startDate,
            endDate,
            dateFilterDisabled,
          },
        );

        if (fetchTableBookingDashboardDataResponse && loadDataRef.current) {
          settableBookingDashboardData(
            fetchTableBookingDashboardDataResponse.table_booking_dashboard,
          );
          setTotalSize(fetchTableBookingDashboardDataResponse.total_size);
          setTotalPages(fetchTableBookingDashboardDataResponse.total_pages);
        }
      } catch (error) {
        console.log(error);
      } finally {
        setLoadingTableBookingDashboardData(false);
      }
    };

    fetchFunc();

    return () => {
      loadDataRef.current = false;
    };
  }, [
    adminUserId,
    from,
    size,
    sort,
    city,
    placeId,
    bookingDate,
    startDate,
    endDate,
    dateFilterDisabled,
  ]);

  return [
    loadingTableBookingDashboardData,
    tableBookingDashboardData,
    totalSize,
    totalPages,
  ];
};

function escapeCSVValue(value) {
  if (value === null || value === undefined) return '';

  if (typeof value === 'boolean') return value ? 'true' : 'false';

  const stringValue = String(value);

  if (
    stringValue.includes(',') ||
    stringValue.includes('"') ||
    stringValue.includes('\n')
  ) {
    return `"${stringValue.replace(/"/g, '""')}"`;
  }

  return stringValue;
}

function generateArray(n) {
  return Array.from(Array(n).keys());
}

export const useTableBookingDashboardCSVData = ({
  adminUserId,
  city = null,
  placeId = null,
  bookingDate = null,
  startDate = null,
  endDate = null,
  dateFilterDisabled = true,
}) => {
  const [
    loadingTableBookingDashboardData,
    setLoadingTableBookingDashboardData,
  ] = React.useState(true);
  const [
    tableBookingDashboardData,
    settableBookingDashboardData,
  ] = React.useState([]);
  const loadDataRef = React.useRef(true);

  React.useEffect(() => {
    loadDataRef.current = true;
    const fetchFunc = async () => {
      try {
        setLoadingTableBookingDashboardData(true);

        const fetchTableBookingDashboardDataResponse = await fetchTableBookingDashboardData(
          {
            adminUserId,
            from: 0,
            size: 50,
            city,
            placeId,
            bookingDate,
            startDate,
            endDate,
            dateFilterDisabled,
          },
        );

        if (!fetchTableBookingDashboardDataResponse) {
          throw new Error('No response from the api!');
        }

        const totals = fetchTableBookingDashboardDataResponse.total_pages || 1;
        let rawData = [
          ...fetchTableBookingDashboardDataResponse.table_booking_dashboard,
        ].map(transformBookingDashboardData);

        if (totals > 1) {
          const pageIndexes = generateArray(totals).slice(1);

          const remainingPagesResponses = await Promise.all(
            pageIndexes.map(pageIndex =>
              fetchTableBookingDashboardData({
                adminUserId,
                from: pageIndex * 50,
                size: 50,
                city,
                placeId,
                bookingDate,
                startDate,
                endDate,
                dateFilterDisabled,
              }),
            ),
          );

          const remainingItems = remainingPagesResponses
            .filter(
              response =>
                response &&
                response.table_booking_dashboard &&
                Array.isArray(response?.table_booking_dashboard) &&
                response?.table_booking_dashboard.length !== 0,
            )
            .flatMap(response => response.table_booking_dashboard)
            .map(transformBookingDashboardData);

          rawData = [...rawData, ...remainingItems];
        }

        if (rawData.length === 0) {
          throw new Error('No data available');
        }

        const headers = Object.keys(rawData[0]);
        const headersCSV = headers.map(escapeCSVValue).join(',');

        const rawDataCSV = rawData.map(item =>
          headers.map(header => escapeCSVValue(item[header])).join(','),
        );

        const contentCSV = [headersCSV, ...rawDataCSV].join('\n');

        settableBookingDashboardData({
          headers,
          headersCSV,
          rawData,
          rawDataCSV,
          contentCSV,
        });
      } catch (error) {
        console.log(error);
      } finally {
        setLoadingTableBookingDashboardData(false);
      }
    };

    fetchFunc();

    return () => {
      loadDataRef.current = false;
    };
  }, [
    adminUserId,
    city,
    placeId,
    bookingDate,
    startDate,
    endDate,
    dateFilterDisabled,
  ]);

  return [loadingTableBookingDashboardData, tableBookingDashboardData];
};

const searchPlaceTableBookingStatisticsQuery = gql`
  query searchPlaceTableBookingStatisticsQuery($input: SearchInput) {
    search_table_booking_dashboard(input: $input) {
      table_booking_dashboard {
        place_name
        total_bookings
        confirmed_count
        cancelled_count
        total_guests
        payment
        refund_amount
        place_id
      }
    }
  }
`;

const transformBookingStatisticsData = bookingSummary => ({
  Name: bookingSummary.place_name,
  Bookings: bookingSummary.total_bookings,
  'Confirmed Bookings': bookingSummary.confirmed_count,
  'Cancelled Bookings': bookingSummary.cancelled_count,
  Guests: bookingSummary.total_guests,
  Payment: !bookingSummary.payment
    ? '-'
    : `$${parseFloat(bookingSummary.payment).toFixed(2)}`,
  Refund: !bookingSummary.refund_amount
    ? '-'
    : `$${Math.abs(parseFloat(bookingSummary.refund_amount)).toFixed(2)}`,
});

export const fetchPlaceTableBookingStatistics = async ({
  // adminUserId,
  from,
  size = 50,
  sort = null,
  placeId = null,
  bookingDate = null,
  startDate = null,
  endDate = null,
  dateFilterDisabled = true,
}) => {
  const input = {
    // user_id: adminUserId,
    from,
    size,
    sort: sort ?? 'PLACE_NAME_ASC',
    filter: {
      table_booking_filter: {},
      place_filter: {},
    },
  };

  if (placeId) {
    input.filter.table_booking_filter.place_id = placeId;
  }

  if (bookingDate) {
    input.filter.table_booking_filter.booking_date = new Date(bookingDate)
      .toLocaleDateString('en-GB')
      .split('/')
      .join('-');
  } else if (startDate && endDate) {
    input.filter.table_booking_filter.date_range = {
      start_date: formatDateFilter(startDate),
      end_date: formatDateFilter(endDate),
    };
  }

  if (dateFilterDisabled) {
    delete input?.filter?.table_booking_filter?.booking_date;
    delete input?.filter?.table_booking_filter?.date_range;
  }

  try {
    const data = await client.clientPublic.query({
      client: client.clientPublic,
      query: searchPlaceTableBookingStatisticsQuery,
      variables: {
        input,
      },
      fetchPolicy: 'network-only',
    });

    if (
      data &&
      data?.data?.search_table_booking_dashboard &&
      Array.isArray(
        data.data.search_table_booking_dashboard?.table_booking_dashboard,
      ) &&
      data.data.search_table_booking_dashboard?.table_booking_dashboard
        .length !== 0
    ) {
      return data.data.search_table_booking_dashboard
        .table_booking_dashboard[0];
    }
    return null;
  } catch (error) {
    console.log(error);
    return null;
  }
};

export const usePlaceTableBookingStatistics = ({
  adminUserId,
  placeId = null,
  bookingDate = null,
  startDate = null,
  endDate = null,
  dateFilterDisabled = true,
}) => {
  const [
    loadingPlaceTableBookingStatistics,
    setLoadingPlaceTableBookingStatistics,
  ] = React.useState(true);
  const [
    placeTableBookingStatistics,
    setPlaceTableBookingStatistics,
  ] = React.useState(null);
  const loadDataRef = React.useRef(true);

  React.useEffect(() => {
    loadDataRef.current = true;
    const fetchFunc = async () => {
      try {
        setLoadingPlaceTableBookingStatistics(true);
        setPlaceTableBookingStatistics(null);

        const fetchPlaceTableBookingStatisticsResponse = await fetchPlaceTableBookingStatistics(
          {
            adminUserId,
            placeId,
            bookingDate,
            startDate,
            endDate,
            dateFilterDisabled,
          },
        );

        if (fetchPlaceTableBookingStatisticsResponse && loadDataRef.current) {
          setPlaceTableBookingStatistics(
            fetchPlaceTableBookingStatisticsResponse,
          );
        }
      } catch (error) {
        console.log(error);
      } finally {
        setLoadingPlaceTableBookingStatistics(false);
      }
    };

    fetchFunc();

    return () => {
      loadDataRef.current = false;
    };
  }, [
    adminUserId,
    placeId,
    bookingDate,
    startDate,
    endDate,
    dateFilterDisabled,
  ]);

  return [loadingPlaceTableBookingStatistics, placeTableBookingStatistics];
};

const fetchPlaceQuery = gql`
  query fetchPlaceQuery($placeId: String!) {
    fetch_place(input: { place_id: $placeId }) {
      is_test_place
      sales_member_info {
        sales_member_id
        sales_member_email
        sales_member_name
      }
      place_id
      name
      slug
      tagline
      keywords
      description
      address_line_1
      address_line_2
      city
      state
      country
      status
      post_code
      timezone
      contact {
        type
        value
        display
        display_order
        is_primary
      }
      latitude
      longitude
    }
  }
`;

export const usePlaceData = ({ placeId }) => {
  const {
    data: fetchPlaceData,
    // loading: fetchPlaceLoading,
    // error: fetchPlaceError,
  } = useQuery(fetchPlaceQuery, {
    variables: { placeId },
    client: client.clientPublic,
  });

  return fetchPlaceData?.fetch_place;
};

const searchTableBookingSummaryQuery = gql`
  query searchTableBookingSummaryQuery($input: SearchInput) {
    search_table_booking_summary(input: $input) {
      total_pages
      total_size
      table_booking_summary {
        table_booking_id
        guest_count
        booking_date
        booking_time
        booking_reference
        booking_name
        phone
        email
        payment
        payout
        c_fees
        tags
        booking_status
        table_number
        payment_status
        billing {
          refund_amount
        }
      }
    }
  }
`;

const transformBookingSummaryData = bookingSummary => ({
  Name: bookingSummary.booking_name,
  Tags: bookingSummary.tags,
  ID: bookingSummary.booking_reference,
  Phone: bookingSummary.phone,
  Email: bookingSummary.email,
  Date: bookingSummary.booking_date,
  Time: bookingSummary.booking_time,
  Status: bookingSummary.booking_status,
  Table: bookingSummary.table_number,
  Guests: bookingSummary.guest_count,
  Payment: !bookingSummary.payment
    ? '-'
    : `$${parseFloat(bookingSummary.payment).toFixed(2)}`,
  'C Fees': !bookingSummary.c_fees
    ? '-'
    : `$${parseFloat(bookingSummary.c_fees).toFixed(2)}`,
  Payout: !bookingSummary.payout
    ? '-'
    : `$${parseFloat(bookingSummary.payout).toFixed(2)}`,
  'Payment Status': bookingSummary.payment_status,
  Refund: !bookingSummary?.billing?.refund_amount
    ? '-'
    : `$${Math.abs(parseFloat(bookingSummary?.billing?.refund_amount)).toFixed(
        2,
      )}`,
});

export const fetchTableBookingSummaryData = async ({
  // adminUserId,
  from,
  size = 50,
  sort = null,
  city = null,
  placeId = null,
  bookingDate = null,
  startDate = null,
  endDate = null,
  dateFilterDisabled = true,
  bookingReference = null,
  bookingName = null,
}) => {
  const input = {
    // user_id: adminUserId,
    from,
    size,
    sort: sort ?? 'PLACE_NAME_ASC',
    filter: {
      table_booking_filter: {},
      place_filter: {},
    },
  };

  if (city) {
    input.filter.table_booking_filter.city = city;
  }

  if (placeId) {
    input.filter.table_booking_filter.place_id = placeId;
  }

  if (bookingReference) {
    input.filter.table_booking_filter.booking_reference = bookingReference;
  }

  if (bookingName) {
    input.filter.table_booking_filter.booking_name = bookingName;
  }

  if (bookingDate) {
    input.filter.table_booking_filter.booking_date = new Date(bookingDate)
      .toLocaleDateString('en-GB')
      .split('/')
      .join('-');
  } else if (startDate && endDate) {
    input.filter.table_booking_filter.date_range = {
      start_date: formatDateFilter(startDate),
      end_date: formatDateFilter(endDate),
    };
  }

  if (dateFilterDisabled) {
    delete input?.filter?.table_booking_filter?.booking_date;
    delete input?.filter?.table_booking_filter?.date_range;
  }

  try {
    const data = await client.clientPublic.query({
      client: client.clientPublic,
      query: searchTableBookingSummaryQuery,
      variables: {
        input,
      },
      fetchPolicy: 'network-only',
    });

    if (
      data &&
      data?.data?.search_table_booking_summary &&
      Array.isArray(
        data.data.search_table_booking_summary?.table_booking_summary,
      ) &&
      data.data.search_table_booking_summary?.table_booking_summary.length !== 0
    ) {
      return data.data.search_table_booking_summary;
    }
    return null;
  } catch (error) {
    console.log(error);
    return null;
  }
};

export const useTableBookingSummaryData = ({
  adminUserId,
  from,
  size = 50,
  sort = null,
  city = null,
  placeId = null,
  bookingDate = null,
  startDate = null,
  endDate = null,
  dateFilterDisabled = true,
  bookingReference = null,
  bookingName = null,
}) => {
  const [
    loadingTableBookingDashboardData,
    setLoadingTableBookingDashboardData,
  ] = React.useState(true);
  const [
    tableBookingDashboardData,
    settableBookingDashboardData,
  ] = React.useState([]);
  const [totalSize, setTotalSize] = React.useState(0);
  const [totalPages, setTotalPages] = React.useState(0);
  const loadDataRef = React.useRef(true);

  React.useEffect(() => {
    loadDataRef.current = true;
    const fetchFunc = async () => {
      try {
        setLoadingTableBookingDashboardData(true);
        settableBookingDashboardData([]);
        setTotalSize(0);
        setTotalPages(0);

        const fetchTableBookingDashboardDataResponse = await fetchTableBookingSummaryData(
          {
            adminUserId,
            from,
            size,
            sort,
            city,
            placeId,
            bookingDate,
            startDate,
            endDate,
            dateFilterDisabled,
            bookingReference,
            bookingName,
          },
        );

        if (fetchTableBookingDashboardDataResponse && loadDataRef.current) {
          settableBookingDashboardData(
            fetchTableBookingDashboardDataResponse.table_booking_summary,
          );
          setTotalSize(fetchTableBookingDashboardDataResponse.total_size);
          setTotalPages(fetchTableBookingDashboardDataResponse.total_pages);
        }
      } catch (error) {
        console.log(error);
      } finally {
        setLoadingTableBookingDashboardData(false);
      }
    };

    fetchFunc();

    return () => {
      loadDataRef.current = false;
    };
  }, [
    adminUserId,
    from,
    size,
    sort,
    city,
    placeId,
    bookingDate,
    startDate,
    endDate,
    dateFilterDisabled,
    bookingReference,
    bookingName,
  ]);

  return [
    loadingTableBookingDashboardData,
    tableBookingDashboardData,
    totalSize,
    totalPages,
  ];
};

export const useTableBookingSummaryCSVData = ({
  adminUserId,
  city = null,
  placeId = null,
  bookingDate = null,
  startDate = null,
  endDate = null,
  dateFilterDisabled = true,
  bookingReference = null,
  bookingName = null,
  sort = null,
}) => {
  const [
    loadingTableBookingDashboardData,
    setLoadingTableBookingDashboardData,
  ] = React.useState(true);
  const [
    tableBookingDashboardData,
    settableBookingDashboardData,
  ] = React.useState([]);
  const loadDataRef = React.useRef(true);

  React.useEffect(() => {
    loadDataRef.current = true;
    const fetchFunc = async () => {
      try {
        setLoadingTableBookingDashboardData(true);

        const fetchTableBookingDashboardDataResponse = await fetchTableBookingSummaryData(
          {
            adminUserId,
            from: 0,
            size: 50,
            city,
            placeId,
            bookingDate,
            startDate,
            endDate,
            dateFilterDisabled,
            bookingReference,
            bookingName,
            sort,
          },
        );

        if (!fetchTableBookingDashboardDataResponse) {
          throw new Error('No response from the api!');
        }

        const totals = fetchTableBookingDashboardDataResponse.total_pages || 1;

        let rawData = [
          ...fetchTableBookingDashboardDataResponse.table_booking_summary.map(
            transformBookingSummaryData,
          ),
        ];

        if (totals > 1) {
          const pageIndexes = generateArray(totals).slice(1);

          const remainingPagesResponses = await Promise.all(
            pageIndexes.map(pageIndex =>
              fetchTableBookingSummaryData({
                adminUserId,
                from: pageIndex * 50,
                size: 50,
                city,
                placeId,
                bookingDate,
                startDate,
                endDate,
                dateFilterDisabled,
                bookingReference,
                bookingName,
                sort,
              }),
            ),
          );

          const remainingItems = remainingPagesResponses
            .filter(
              response =>
                response &&
                response.table_booking_summary &&
                Array.isArray(response?.table_booking_summary) &&
                response?.table_booking_summary.length !== 0,
            )
            .flatMap(response => response.table_booking_summary)
            .map(transformBookingSummaryData);

          rawData = [...rawData, ...remainingItems];
        }

        if (rawData.length === 0) {
          throw new Error('No data available');
        }

        const headers = Object.keys(rawData[0]);
        const headersCSV = headers.map(escapeCSVValue).join(',');

        const rawDataCSV = rawData.map(item =>
          headers.map(header => escapeCSVValue(item[header])).join(','),
        );

        const contentCSV = [headersCSV, ...rawDataCSV].join('\n');

        settableBookingDashboardData({
          headers,
          headersCSV,
          rawData,
          rawDataCSV,
          contentCSV,
        });
      } catch (error) {
        console.log(error);
      } finally {
        setLoadingTableBookingDashboardData(false);
      }
    };

    fetchFunc();

    return () => {
      loadDataRef.current = false;
    };
  }, [
    adminUserId,
    city,
    placeId,
    bookingDate,
    startDate,
    endDate,
    dateFilterDisabled,
    bookingReference,
    bookingName,
    sort,
  ]);

  return [loadingTableBookingDashboardData, tableBookingDashboardData];
};

export const usePlaceTableBookingStatisticsCSVData = ({
  adminUserId,
  placeId = null,
  bookingDate = null,
  startDate = null,
  endDate = null,
  dateFilterDisabled = true,
}) => {
  const [
    loadingTableBookingDashboardData,
    setLoadingTableBookingDashboardData,
  ] = React.useState(true);
  const [
    tableBookingDashboardData,
    settableBookingDashboardData,
  ] = React.useState([]);
  const loadDataRef = React.useRef(true);

  React.useEffect(() => {
    loadDataRef.current = true;
    const fetchFunc = async () => {
      try {
        setLoadingTableBookingDashboardData(true);

        const fetchTableBookingDashboardDataResponse = await fetchPlaceTableBookingStatistics(
          {
            adminUserId,
            from: 0,
            size: 50,
            placeId,
            bookingDate,
            startDate,
            endDate,
            dateFilterDisabled,
          },
        );

        if (!fetchTableBookingDashboardDataResponse) {
          throw new Error('No response from the api!');
        }

        const rawData = [fetchTableBookingDashboardDataResponse].map(
          transformBookingStatisticsData,
        );

        if (rawData.length === 0) {
          throw new Error('No data available');
        }

        const headers = Object.keys(rawData[0]);
        const headersCSV = headers.map(escapeCSVValue).join(',');

        const rawDataCSV = rawData.map(item =>
          headers.map(header => escapeCSVValue(item[header])).join(','),
        );

        const contentCSV = [headersCSV, ...rawDataCSV].join('\n');

        settableBookingDashboardData({
          headers,
          headersCSV,
          rawData,
          rawDataCSV,
          contentCSV,
        });
      } catch (error) {
        console.log(error);
      } finally {
        setLoadingTableBookingDashboardData(false);
      }
    };

    fetchFunc();

    return () => {
      loadDataRef.current = false;
    };
  }, [
    adminUserId,
    placeId,
    bookingDate,
    startDate,
    endDate,
    dateFilterDisabled,
  ]);

  return [loadingTableBookingDashboardData, tableBookingDashboardData];
};

export default {};
